/**
 * Auth Context
 *
 * Firebase Auth state provider. Listens to auth changes,
 * loads officer profile, sets account state in session store.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { initFirebaseClient } from '@copapp/firebase/client';
import { COLLECTIONS } from '@copapp/core/constants';
import type { OfficerProfile } from '@copapp/core/types';
import { useSessionStore, useSimulationStore } from '../stores';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
    opts?: { pendingInvite?: boolean },
  ) => Promise<User>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setOfficer, setAccountState, setInitialized, reset: resetSession } = useSessionStore();

  useEffect(() => {
    const { auth, db } = initFirebaseClient();

    // The profile listener must be torn down BY HAND on every auth change:
    // a return value from the onAuthStateChanged callback is ignored, so the
    // old pattern leaked one listener per account switch — spraying
    // permission-denied and resurrecting the previous account's profile.
    let unsubProfile: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }
      setUser(firebaseUser);

      if (!firebaseUser) {
        resetSession();
        // Signed out: any in-flight simulation state dies with the session.
        useSimulationStore.getState().resetSimulation();
        setIsLoading(false);
        setInitialized();
        return;
      }

      // Cross-account guard: persisted in-flight simulation state belongs to
      // exactly one officer. The same user refreshing mid-call keeps their
      // recovery; anyone else gets a clean slate.
      const persisted = useSimulationStore.getState();
      if (persisted.sessionId && persisted.officerUid !== firebaseUser.uid) {
        useSimulationStore.getState().resetSimulation();
      }

      // Listen to officer profile
      const officerRef = doc(db, COLLECTIONS.OFFICERS, firebaseUser.uid);
      unsubProfile = onSnapshot(officerRef, (snap) => {
        if (snap.exists()) {
          const officer = { uid: snap.id, ...snap.data() } as OfficerProfile;
          setOfficer(officer);
          setAccountState(officer.accountState);
        } else {
          setOfficer(null);
          setAccountState('none');
        }
        setIsLoading(false);
        setInitialized();
      }, (err) => {
        // Never let a profile-listener error take down the SDK — degrade
        // to signed-in-without-profile and let the UI's guards handle it.
        console.warn('profile listener error:', err.code);
        setIsLoading(false);
        setInitialized();
      });
    });

    return () => {
      if (unsubProfile) unsubProfile();
      unsubAuth();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { auth, functions } = initFirebaseClient();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      httpsCallable(functions, 'logAuthEvent')({ eventType: 'login', email, success: true }).catch(() => {});
    } catch (err: unknown) {
      const errorCode = (err as { code?: string }).code ?? 'unknown';
      httpsCallable(functions, 'logAuthEvent')({ eventType: 'failed_login', email, success: false, errorCode }).catch(() => {});
      throw err;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    displayName?: string,
    opts?: { pendingInvite?: boolean },
  ) => {
    const { auth, functions } = initFirebaseClient();
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    // The officer profile is the account's spine — lifecycle, trial clock,
    // roster, language all hang off it. Create it before proceeding.
    // pendingInvite steers the email fork: invite-path signups skip the
    // trial welcome (joinDepartmentWithInvite sends "you're covered").
    try {
      await httpsCallable(functions, 'onUserCreated')({
        displayName,
        email,
        pendingInvite: opts?.pendingInvite === true,
      });
    } catch (err) {
      console.error('Officer profile creation failed (will degrade until retried):', err);
    }
    // Mirror the name onto the AUTH profile too — surfaces that read
    // user.displayName (not the officer doc) otherwise see undefined
    // until the Settings editor's first save.
    if (displayName?.trim()) {
      updateProfile(cred.user, { displayName: displayName.trim() }).catch(() => {});
    }
    httpsCallable(functions, 'logAuthEvent')({ eventType: 'signup', email, success: true }).catch(() => {});
    return cred.user;
  };

  const signOut = async () => {
    const { auth, functions } = initFirebaseClient();
    httpsCallable(functions, 'logAuthEvent')({ eventType: 'logout', success: true }).catch(() => {});
    await firebaseSignOut(auth);
    resetSession();
  };

  const resetPassword = async (email: string) => {
    const { auth, functions } = initFirebaseClient();
    await sendPasswordResetEmail(auth, email);
    httpsCallable(functions, 'logAuthEvent')({ eventType: 'password_reset', email, success: true }).catch(() => {});
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
